/**
 * JST日付ユーティリティ（フロントエンド用）
 *
 * ブラウザはユーザーのローカルTZで動作するため、
 * 日本国内利用を前提にローカルTZ基準で日付文字列を生成する。
 */

/**
 * 今日の日付をYYYY-MM-DD形式で返す（ローカルTZ基準）
 */
export function todayDateString(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * 今日の日付をYYYYMMDD形式で返す（ファイル名用）
 */
export function todayCompact(date = new Date()): string {
  return todayDateString(date).replace(/-/g, '');
}

/**
 * 今日の日付をYYYY-MM-DD形式で返す（JST固定。閲覧PCのTZに依存しない）
 * DB・APIをJSTへ統一したため、フロントの「今日」判定もJSTで行う。
 */
export function todayStringJST(date = new Date()): string {
  // sv-SE ロケールは YYYY-MM-DD 形式で出力される
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Tokyo' }).format(date);
}

/**
 * 日時をJST(Asia/Tokyo)で「YYYY/MM/DD HH:mm」に整形する共通関数。
 * - value: Date | ISO文字列 | epoch(number) | null/undefined
 * - 空・不正値は '' を返す（画面側で '-' 等にフォールバックしやすいよう空文字）
 * - withSeconds=true で秒まで表示
 * 閲覧PCのTZに関わらず常にJST壁時計で表示する。
 */
export function formatDateTimeJST(
  value: Date | string | number | null | undefined,
  withSeconds = false
): string {
  if (value === null || value === undefined || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return typeof value === 'string' ? value : '';
  return d.toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
    hour12: false,
  });
}

/**
 * 日付をJST(Asia/Tokyo)で「YYYY/MM/DD」に整形する共通関数。
 * - 'YYYY-MM-DD' の日付のみ文字列はTZ変換せずそのまま整形（DATE列対応）。
 * - それ以外は Date として解釈しJSTで整形。
 */
export function formatDateJST(
  value: Date | string | number | null | undefined
): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value.replace(/-/g, '/');
  }
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return typeof value === 'string' ? value : '';
  return d.toLocaleDateString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}
